import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronRight, FiShield } from "react-icons/fi";
import {
  BuyerBidV2EspdAnswerDto,
  type BuyerBidV2EspdCategoryDto,
  type BuyerBidV2EspdCriterionDto,
  type BuyerBidV2EspdDto,
  type BuyerBidV2EspdRequirementDto,
  type BuyerBidV2EspdRequirementGroupDto,
  type BuyerBidV2EspdRequirementValueDto,
} from "../../../../autogen/bff-api";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";
import {
  EspdEmptyState,
  espdCategoryHasIssue,
  espdCategoryName,
  espdStatus,
  MutedValue,
  RequirementGroup,
  StatusPill,
  localizedText,
  safeId,
  toneAccent,
  toneBg,
  toneBorder,
  toneText,
  type Tone,
} from "./espdResponseRendering";
import { useDefaultedExpansion } from "./useDefaultedExpansion";

interface Props {
  espd: BuyerBidV2EspdDto | null | undefined;
}

export const EspdTab = ({ espd }: Props) => {
  const { t } = useTranslation();
  const categories = useMemo(() => espd?.categories ?? [], [espd?.categories]);
  const defaultExpandedCategories = useMemo(
    () => categories.filter((category) => espdCategoryHasIssue(category)).map((category) => category.code),
    [categories],
  );
  const defaultExpandedCriteria = useMemo(
    () =>
      categories.flatMap((category) =>
        category.criteria
          .filter((criterion) => criterion.answer !== BuyerBidV2EspdAnswerDto.Ok)
          .map((criterion) => criterionKey(category, criterion)),
      ),
    [categories],
  );
  const categoryIds = useMemo(() => categories.map((category) => category.code), [categories]);
  const criterionIds = useMemo(
    () => categories.flatMap((category) => category.criteria.map((criterion) => criterionKey(category, criterion))),
    [categories],
  );
  const resetKey = useMemo(() => categoryIds.join(""), [categoryIds]);
  const { expanded: expandedCategories, toggle: toggleCategory } = useDefaultedExpansion({
    ids: categoryIds,
    defaultExpanded: defaultExpandedCategories,
    resetKey,
  });
  const { expanded: expandedCriteria, toggle: toggleCriterion } = useDefaultedExpansion({
    ids: criterionIds,
    defaultExpanded: defaultExpandedCriteria,
    resetKey,
  });

  if (!espd?.hasResponse) {
    return <EspdEmptyState />;
  }

  const { label: totalStatus, tone: totalTone } = espdStatus(espd.deviationCount, espd.unansweredCount, t);

  const focusCategory = (category: BuyerBidV2EspdCategoryDto) => {
    const targetCriterion = firstIssue(category);

    toggleCategory(category.code, { force: "open" });
    if (targetCriterion) {
      toggleCriterion(criterionKey(category, targetCriterion), { force: "open" });
    }
    scrollToElement(categoryButtonId(category));
  };

  return (
    <Stack spacing="5">
      <SectionCard icon={FiShield} title={t("Exclusion grounds – Part III")}>
        <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="4" mb="4" flexWrap="wrap">
          <Text color="smMuted" fontSize="sm" maxW="72ch">
            {t("Exclusion grounds from the submitted ESPD response.")}
          </Text>
          <StatusPill tone={totalTone}>{totalStatus}</StatusPill>
        </Flex>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="3">
          {categories.map((category) => (
            <CategorySummaryButton key={category.code} category={category} onClick={() => focusCategory(category)} />
          ))}
        </SimpleGrid>
      </SectionCard>

      <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap="3" flexWrap="wrap">
        <Box>
          <Text fontWeight="bold" color="smText">
            {t("Detailed responses")}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("Expand a question to inspect the submitted answers.")}
          </Text>
        </Box>
        <Text fontSize="sm" color="smMuted">
          {t("Read-only")}
        </Text>
      </Flex>

      <Stack spacing="3">
        {categories.map((category) => {
          const isOpen = expandedCategories.includes(category.code);
          return (
            <CategorySection
              key={category.code}
              category={category}
              isOpen={isOpen}
              onToggle={() => toggleCategory(category.code)}
            >
              {category.criteria.map((criterion) => {
                const key = criterionKey(category, criterion);
                return (
                  <CriterionSection
                    key={key}
                    category={category}
                    criterion={criterion}
                    isOpen={expandedCriteria.includes(key)}
                    onToggle={() => toggleCriterion(key)}
                  />
                );
              })}
            </CategorySection>
          );
        })}
      </Stack>
    </Stack>
  );
};

const CategorySummaryButton = ({
  category,
  onClick,
}: {
  category: BuyerBidV2EspdCategoryDto;
  onClick: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const { label: status, tone } = espdStatus(category.deviationCount, category.unansweredCount, t);
  const categoryName = espdCategoryName(category, t);
  const issue = firstIssue(category);
  const firstIssueLabel = issue
    ? localizedText(i18n.language, issue.nameSource, issue.nameTranslation, issue.name) || null
    : null;
  const meta = firstIssueLabel ?? t("espd_question_count", { count: category.criteria.length });

  return (
    <Button
      type="button"
      variant="unstyled"
      h="auto"
      minH="88px"
      display="grid"
      alignContent="space-between"
      alignItems="stretch"
      justifyContent="stretch"
      justifyItems="stretch"
      textAlign="left"
      whiteSpace="normal"
      border="1px solid"
      borderColor={toneBorder(tone)}
      bg={toneBg(tone)}
      borderRadius="md"
      p="3"
      onClick={onClick}
      _hover={{ borderColor: toneAccent(tone), transform: "translateY(-1px)" }}
      _focusVisible={{ boxShadow: "smFocusRing" }}
    >
      <Box minW="0">
        <Text fontWeight="bold" color="smText" noOfLines={2} overflowWrap="anywhere">
          {categoryName}
        </Text>
        <Text mt="0.5" fontSize="sm" color="smMuted" noOfLines={1} overflowWrap="anywhere">
          {meta}
        </Text>
      </Box>
      <Text fontSize="sm" fontWeight="bold" color={toneText(tone)}>
        {status}
      </Text>
    </Button>
  );
};

const CategorySection = ({
  category,
  isOpen,
  onToggle,
  children,
}: {
  category: BuyerBidV2EspdCategoryDto;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const { label: status, tone } = espdStatus(category.deviationCount, category.unansweredCount, t);
  const categoryName = espdCategoryName(category, t);
  const buttonId = categoryButtonId(category);
  const panelId = categoryPanelId(category);

  return (
    <Box
      id={categoryElementId(category)}
      bg="smEspdCategoryBg"
      border="1px solid"
      borderColor="smBorder"
      borderRadius="md"
      overflow="hidden"
    >
      <Button
        id={buttonId}
        type="button"
        variant="unstyled"
        h="auto"
        w="full"
        display="block"
        textAlign="left"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <Flex
          align={{ base: "flex-start", sm: "center" }}
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          gap="3"
          px={{ base: "3", md: "4" }}
          py="3"
          bg="smEspdCategoryBg"
        >
          <Box minW="0">
            <Text fontWeight="bold" color="smText" noOfLines={2} overflowWrap="anywhere">
              {categoryName}
            </Text>
            <Text fontSize="sm" color="smMuted">
              {t("espd_question_count", { count: category.criteria.length })}
            </Text>
          </Box>
          <HStack spacing="3" flexShrink={0} alignSelf={{ base: "flex-end", sm: "center" }}>
            <StatusPill tone={tone}>{status}</StatusPill>
            <Icon as={isOpen ? FiChevronDown : FiChevronRight} color="smMuted" />
          </HStack>
        </Flex>
      </Button>
      {isOpen && (
        <Box
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          px={{ base: "3", md: "4" }}
          py="3"
          bg="smEspdCategoryBg"
        >
          <Stack spacing="2">{children}</Stack>
        </Box>
      )}
    </Box>
  );
};

const CriterionSection = ({
  category,
  criterion,
  isOpen,
  onToggle,
}: {
  category: BuyerBidV2EspdCategoryDto;
  criterion: BuyerBidV2EspdCriterionDto;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const tone = answerTone(criterion.answer);
  const criterionName = localizedText(i18n.language, criterion.nameSource, criterion.nameTranslation, criterion.name);
  const criterionDescription = localizedText(
    i18n.language,
    criterion.descriptionSource,
    criterion.descriptionTranslation,
    criterion.description,
  );
  const buttonId = criterionElementId(category, criterion);
  const panelId = criterionPanelId(category, criterion);

  return (
    <Box
      border="1px solid"
      borderColor="smBorder"
      borderRadius="md"
      overflow="hidden"
      bg="smEspdCriterionBg"
    >
      <Button
        id={buttonId}
        type="button"
        variant="unstyled"
        h="auto"
        w="full"
        display="block"
        textAlign="left"
        whiteSpace="normal"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        _focusVisible={{ boxShadow: "smFocusRing", zIndex: 1 }}
      >
        <Box
          data-testid="espd-criterion-row-grid"
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "minmax(0, 1fr) max-content" }}
          alignItems={{ base: "stretch", md: "center" }}
          gap="3"
          px={{ base: "3", md: "4" }}
          py="3"
          bg="smEspdCriterionBg"
        >
          <Box minW="0" data-testid="espd-criterion-copy">
            <Text fontWeight="bold" color="smText" noOfLines={{ base: 3, md: 2 }} overflowWrap="anywhere">
              {criterionName}
            </Text>
            {criterionDescription && (
              <Text mt="0.5" fontSize="sm" color="smMuted" noOfLines={{ base: 3, md: 2 }} overflowWrap="anywhere">
                {criterionDescription}
              </Text>
            )}
          </Box>
          <HStack
            data-testid="espd-criterion-answer-status"
            spacing="3"
            justifySelf={{ base: "start", md: "end" }}
            flexShrink={0}
          >
            <StatusPill tone={tone}>{answerLabel(criterion.answer, t)}</StatusPill>
            <Icon as={isOpen ? FiChevronDown : FiChevronRight} color="smMuted" />
          </HStack>
        </Box>
      </Button>
      {isOpen && (
        <Stack
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          spacing="4"
          px={{ base: "3", md: "4" }}
          py="4"
          bg="smBackground"
          borderTop="1px solid"
          borderColor="smBorder"
        >
          {criterion.responseGroups.length === 0 ? (
            <MutedValue>{t("No answer details")}</MutedValue>
          ) : (
            criterion.responseGroups.map((group) => <RequirementGroup key={group.id} group={group} />)
          )}
        </Stack>
      )}
    </Box>
  );
};

const firstIssue = (category: BuyerBidV2EspdCategoryDto) =>
  category.criteria.find(
    (criterion) =>
      criterion.answer === BuyerBidV2EspdAnswerDto.Deviation || criterion.answer === BuyerBidV2EspdAnswerDto.Unanswered,
  );



const answerTone = (answer: BuyerBidV2EspdAnswerDto): Tone => {
  if (answer === BuyerBidV2EspdAnswerDto.Deviation) return "issue";
  if (answer === BuyerBidV2EspdAnswerDto.Unanswered) return "neutral";
  return "ok";
};

const answerLabel = (answer: BuyerBidV2EspdAnswerDto, t: (value: string) => string) => {
  if (answer === BuyerBidV2EspdAnswerDto.Deviation) return t("Deviation");
  if (answer === BuyerBidV2EspdAnswerDto.Unanswered) return t("Not answered");
  return t("OK");
};


const categoryElementId = (category: BuyerBidV2EspdCategoryDto) => `espd-category-${safeId(category.code)}`;
const categoryButtonId = (category: BuyerBidV2EspdCategoryDto) => `espd-category-button-${safeId(category.code)}`;
const categoryPanelId = (category: BuyerBidV2EspdCategoryDto) => `espd-category-panel-${safeId(category.code)}`;

const criterionElementId = (category: BuyerBidV2EspdCategoryDto, criterion: BuyerBidV2EspdCriterionDto) =>
  `espd-criterion-${safeId(category.code)}-${safeId(criterion.id)}`;

const criterionPanelId = (category: BuyerBidV2EspdCategoryDto, criterion: BuyerBidV2EspdCriterionDto) =>
  `espd-criterion-panel-${safeId(category.code)}-${safeId(criterion.id)}`;

const criterionKey = (category: BuyerBidV2EspdCategoryDto, criterion: BuyerBidV2EspdCriterionDto) =>
  `${category.code}:${criterion.id}`;

const scrollToElement = (id: string) => {
  const scroll = () => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
    element?.focus({ preventScroll: true });
  };

  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(scroll);
  } else {
    window.setTimeout(scroll, 0);
  }
};
